import { generatePath } from 'react-router-dom'
import { CompanyTag } from '@/components/ui/badge'
import { GridCellProps, GridColumnProps } from '@progress/kendo-react-grid'
import { TradingAccountKey } from 'api/Clients/Types/TradingAccount'
import { Link } from 'components/Link'
import { formatLocalDateUTC } from 'utils/date'
import { links } from 'utils/featureToggles'

import '../TradingAccount.scss'

export interface GridColumn extends GridColumnProps {
  show: boolean
  width?: number
}

export const liveTradingAccountColumns: GridColumn[] = [
  {
    title: 'Name',
    field: TradingAccountKey.name,
    cell: (props: GridCellProps): JSX.Element => {
      return (
        <td style={{ color: 'var(--link)', textDecoration: 'underline' }}>
          <Link
            to={{
              pathname: generatePath(links.recordsTradingAccounts.detailsUrl as string, {
                id: props.dataItem.id,
                slug: 'details',
              }),
            }}
          >
            {props.dataItem.name}
          </Link>
        </td>
      )
    },
    filter: 'text',
    show: true,
    className: 'trading-account-table-columns__content',
    width: 120,
  },
  {
    title: 'Wallet',
    field: TradingAccountKey.walletName,
    cell: (props: GridCellProps) => (
      <td style={{ color: 'var(--link)', textDecoration: 'underline' }}>
        <Link
          to={{
            pathname: generatePath(links.recordsWallets.detailsUrl as string, {
              id: props.dataItem.wallet.id,
              slug: 'details',
            }),
          }}
        >
          {props.dataItem.wallet.name}
        </Link>
      </td>
    ),
    filter: 'text',
    show: true,
    filterable: true,
    className: 'trading-account-table-columns__content',
    width: 150,
  },
  {
    title: 'Client',
    field: TradingAccountKey.clientName,
    cell: (props: GridCellProps) => (
      <td style={{ color: 'var(--link)', textDecoration: 'underline' }}>
        <Link
          to={{
            pathname: generatePath(links.recordsClients.detailsUrl as string, {
              id: props.dataItem.client.id,
              slug: 'details',
            }),
          }}
        >
          {props.dataItem.client.name}
        </Link>
      </td>
    ),
    filter: 'text',
    show: true,
    filterable: true,
    className: 'trading-account-table-columns__content',
  },
  {
    title: 'Introducing Broker',
    field: TradingAccountKey.introducingBrokerName,
    cell: (props: GridCellProps): JSX.Element => {
      if (!props.dataItem?.introducingBroker?.id) {
        return <td />
      }
      return (
        <td style={{ color: 'var(--link)', textDecoration: 'underline' }}>
          <Link
            to={{
              pathname: generatePath(links.recordsIntroducingBrokers.detailsUrl as string, {
                id: props.dataItem.introducingBroker.id,
                slug: 'details',
              }),
            }}
          >
            {props.dataItem?.introducingBroker?.name}
          </Link>
        </td>
      )
    },
    filter: 'text',
    show: true,
    filterable: true,
    className: 'trading-account-table-columns__content',
  },
  {
    title: 'Account Group',
    field: TradingAccountKey.accountGroupName,
    filter: 'text',
    show: true,
    filterable: true,
    className: 'trading-account-table-columns__content',
  },
  {
    title: 'Account Type',
    field: TradingAccountKey.accountTypeName,
    filter: 'text',
    show: true,
    filterable: true,
    className: 'trading-account-table-columns__content',
  },
  {
    title: 'Platform Type',
    field: TradingAccountKey.platformType,
    filter: 'text',
    show: true,
    filterable: true,
    className: 'trading-account-table-columns__content',
  },
  {
    title: 'Currency',
    field: TradingAccountKey.currencyId,
    filter: 'text',
    show: true,
    filterable: true,
    className: 'trading-account-table-columns__content',
  },
  {
    title: 'Status',
    field: TradingAccountKey.statusName,
    filter: 'text',
    show: true,
    filterable: true,
    className: 'trading-account-table-columns__content',
  },
  {
    title: 'TV',
    field: TradingAccountKey.isTradingViewConnected,
    cell: (props: GridCellProps): JSX.Element => {
      const isTradingViewConnected = props.dataItem.isTradingViewConnected
      if (isTradingViewConnected === null) {
        return <td style={{ textAlign: 'center' }}>N/A</td>
      }

      return (
        <td style={{ textAlign: 'center' }}>
          <input type='checkbox' checked={isTradingViewConnected} disabled />
        </td>
      )
    },
    show: true,
    filterable: true,
    className: 'trading-account-table-columns__content',
  },
  {
    title: 'Country',
    field: TradingAccountKey.countryName,
    filter: 'text',
    show: true,
    filterable: true,
    className: 'trading-account-table-columns__content',
  },
  {
    title: 'Entity',
    field: TradingAccountKey.tickmillCompanyName,
    cell: (props: GridCellProps): JSX.Element => {
      if (!props.dataItem?.tickmillCompany?.id) {
        return <td />
      }
      return (
        <td>
          <CompanyTag id={props.dataItem.tickmillCompany.id}>
            {props.dataItem.tickmillCompany.name}
          </CompanyTag>
        </td>
      )
    },
    show: true,
    filterable: true,
    filter: 'text',
    className: 'trading-account-table-columns__content',
    width: 80,
    minResizableWidth: 80,
  },
  {
    title: 'Date Created',
    field: TradingAccountKey.createdDate,
    cell: (props: GridCellProps) => (
      <td className='table-columns__date'>
        {formatLocalDateUTC(props.dataItem.createdDate, true)}
      </td>
    ),
    filter: 'text',
    show: true,
    filterable: true,
    width: 100,
    className: 'wallet-table-columns__content',
  },
  {
    title: 'Date Updated',
    field: TradingAccountKey.updatedDate,
    cell: (props: GridCellProps) => (
      <td className='table-columns__date'>
        {formatLocalDateUTC(props.dataItem.updatedDate, true)}
      </td>
    ),
    filter: 'text',
    show: true,
    filterable: true,
    width: 100,
    className: 'wallet-table-columns__content',
  },
]
